import apiClient from './client';
import { buildQueryString, type BaseListParams } from './utils';
import type {
  Participant,
  ParticipantStatus,
  CSVColumnMapping,
  CSVImportPreview,
  PaginatedResponse,
  CohortHistoryEntry,
  EmailHistoryEntry,
  AbsenceEmailHistoryEntry,
  ParticipantCohortAttendance,
} from '../../types';

// Assigned status filter
export type AssignedStatus = 'all' | 'assigned' | 'unassigned';

// List params for filtering participants
export interface ParticipantListParams extends BaseListParams {
  status?: ParticipantStatus;
  groupId?: string;
  personalityType?: string;
  assignedStatus?: AssignedStatus;
  flaggedOnly?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// Request to create a single participant
export interface CreateParticipantRequest {
  email: string;
  first_name: string;
  last_name: string;
  preferred_name?: string;
  personality_type?: string;
  role?: string;
  business_unit?: string;
  location?: string;
  manager?: string;
  manager_email?: string;
  level?: string;
  dnm?: string;
  dnm_groupings?: string;
  custom_fields?: Record<string, string>;
}

// Request to update a participant (cohort-specific fields)
export interface UpdateParticipantRequest {
  personality_type?: string;
  role?: string;
  business_unit?: string;
  location?: string;
  manager?: string;
  manager_email?: string;
  level?: string;
  dnm?: string;
  dnm_groupings?: string;
  custom_fields?: Record<string, string>;
  status?: ParticipantStatus;
  group_id?: string | null;
  withdrawal_reason?: string;
}

// Request to update participant core identity
export interface UpdateIdentityRequest {
  first_name?: string;
  last_name?: string;
  email?: string;
}

// CSV import preview request
export interface CSVImportPreviewRequest {
  mappings: CSVColumnMapping[];
  csvData: string[][];
}

// CSV import execute request
export interface CSVImportRequest extends CSVImportPreviewRequest {
  skipDuplicates?: boolean;
}

// CSV import result
export interface CSVImportResult {
  success: boolean;
  imported: number;
  updated: number;
  skipped: number;
  usersCreated: number;
  customFieldsCreated: string[];
  errors: Array<{ row: number; error: string }>;
}

// Summary stats from API
export interface ParticipantSummary {
  total: number;
  active: number;
  pending: number;
  withdrawn: number;
  unassigned: number;
  avg_attendance: number | null;
}

// Custom field definition
export interface CohortCustomFieldDefinition {
  id: string;
  cohort_id: string;
  field_name: string;
  field_label: string;
  field_type: 'text' | 'number' | 'date' | 'boolean';
  sort_order: number;
}

// Extended paginated response with summary and custom fields
export interface ParticipantListResponse extends PaginatedResponse<Participant> {
  summary: ParticipantSummary;
  customFields: CohortCustomFieldDefinition[];
}

export const participantsApi = {
  /**
   * Get participants for a cohort (lightweight version using apiClient with token refresh)
   * Use this for simple data fetching where summary/customFields aren't needed
   */
  listBasic: async (
    cohortId: string,
    params?: ParticipantListParams,
  ): Promise<PaginatedResponse<Participant>> => {
    const url = `/api/cohorts/${cohortId}/participants${buildQueryString(params)}`;
    return apiClient.getPaginated<Participant>(url);
  },

  /**
   * Get participants for a cohort with summary and custom field metadata
   */
  list: async (cohortId: string, params?: ParticipantListParams): Promise<ParticipantListResponse> => {
    const url = `/api/cohorts/${cohortId}/participants${buildQueryString(params)}`;
    const response = await apiClient.rawGetWithMeta<Participant[]>(url);
    const meta = response.meta as {
      pagination: PaginatedResponse<Participant>['pagination'];
      summary: ParticipantSummary;
      customFields: CohortCustomFieldDefinition[];
    } | undefined;

    return {
      data: response.data,
      pagination: meta?.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
      summary: meta?.summary || { total: 0, active: 0, pending: 0, withdrawn: 0, unassigned: 0, avg_attendance: null },
      customFields: meta?.customFields || [],
    };
  },

  /**
   * Get single participant by ID
   */
  getById: async (cohortId: string, participantId: string): Promise<Participant> => {
    return apiClient.get<Participant>(`/api/cohorts/${cohortId}/participants/${participantId}`);
  },

  /**
   * Add single participant to cohort
   */
  create: async (cohortId: string, data: CreateParticipantRequest): Promise<Participant & { userCreated: boolean }> => {
    return apiClient.post<Participant & { userCreated: boolean }>(`/api/cohorts/${cohortId}/participants`, data);
  },

  /**
   * Update participant
   */
  update: async (cohortId: string, participantId: string, data: UpdateParticipantRequest): Promise<Participant> => {
    return apiClient.put<Participant>(`/api/cohorts/${cohortId}/participants/${participantId}`, data);
  },

  /**
   * Remove participant from cohort
   */
  delete: async (cohortId: string, participantId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/participants/${participantId}`);
  },

  /**
   * Withdraw a participant from the cohort (soft status change)
   * Sets status to 'withdrawn', preserves historical data
   */
  withdraw: async (cohortId: string, participantId: string): Promise<Participant> => {
    return apiClient.patch<Participant>(`/api/cohorts/${cohortId}/participants/${participantId}/withdraw`);
  },

  /**
   * Re-activate a withdrawn participant
   * Restores status to 'active', clears withdrawal date
   */
  reactivate: async (cohortId: string, participantId: string): Promise<Participant> => {
    return apiClient.patch<Participant>(`/api/cohorts/${cohortId}/participants/${participantId}/reactivate`);
  },

  /**
   * Get full cohort history for a participant with attendance statistics
   */
  getCohortHistory: async (cohortId: string, participantId: string): Promise<CohortHistoryEntry[]> => {
    return apiClient.get<CohortHistoryEntry[]>(`/api/cohorts/${cohortId}/participants/${participantId}/cohort-history`);
  },

  /**
   * Get email history for a participant within a cohort
   */
  getEmailHistory: async (cohortId: string, participantId: string): Promise<EmailHistoryEntry[]> => {
    return apiClient.get<EmailHistoryEntry[]>(`/api/cohorts/${cohortId}/participants/${participantId}/email-history`);
  },

  /**
   * Get absence email history for a participant within a cohort (E19-4: MED-500)
   */
  getAbsenceEmailHistory: async (cohortId: string, participantId: string): Promise<AbsenceEmailHistoryEntry[]> => {
    return apiClient.get<AbsenceEmailHistoryEntry[]>(`/api/cohorts/${cohortId}/participants/${participantId}/absence-email-history`);
  },

  /**
   * Get cohort attendance summary and engagement ratings for a participant
   */
  getCohortAttendance: async (cohortId: string, participantId: string): Promise<ParticipantCohortAttendance> => {
    return apiClient.get<ParticipantCohortAttendance>(`/api/cohorts/${cohortId}/participants/${participantId}/cohort-attendance`);
  },

  /**
   * Update participant core identity (first_name, last_name, email)
   * This affects all cohort enrollments for this user
   */
  updateIdentity: async (cohortId: string, participantId: string, data: UpdateIdentityRequest): Promise<Participant> => {
    return apiClient.put<Participant>(`/api/cohorts/${cohortId}/participants/${participantId}/identity`, data);
  },

  /**
   * Preview CSV import - validates data and returns preview
   */
  previewImport: async (cohortId: string, data: CSVImportPreviewRequest): Promise<CSVImportPreview> => {
    return apiClient.post<CSVImportPreview>(`/api/cohorts/${cohortId}/participants/import/preview`, data);
  },

  /**
   * Execute CSV import
   */
  executeImport: async (cohortId: string, data: CSVImportRequest): Promise<CSVImportResult> => {
    return apiClient.post<CSVImportResult>(`/api/cohorts/${cohortId}/participants/import`, data);
  },

  // ============================================================================
  // Custom Field Definition Methods
  // ============================================================================

  /**
   * Get all custom field definitions for a cohort
   */
  listCustomFields: async (cohortId: string): Promise<CohortCustomFieldDefinition[]> => {
    return apiClient.get<CohortCustomFieldDefinition[]>(`/api/cohorts/${cohortId}/custom-fields`);
  },

  /**
   * Create a custom field definition
   */
  createCustomField: async (
    cohortId: string,
    data: { field_name: string; field_label: string; field_type?: 'text' | 'number' | 'date' | 'boolean' },
  ): Promise<CohortCustomFieldDefinition> => {
    return apiClient.post<CohortCustomFieldDefinition>(`/api/cohorts/${cohortId}/custom-fields`, data);
  },

  /**
   * Update a custom field definition
   */
  updateCustomField: async (
    cohortId: string,
    fieldId: string,
    data: { field_label?: string; field_type?: 'text' | 'number' | 'date' | 'boolean' },
  ): Promise<CohortCustomFieldDefinition> => {
    return apiClient.put<CohortCustomFieldDefinition>(`/api/cohorts/${cohortId}/custom-fields/${fieldId}`, data);
  },

  /**
   * Delete a custom field definition
   */
  deleteCustomField: async (cohortId: string, fieldId: string): Promise<void> => {
    return apiClient.delete<void>(`/api/cohorts/${cohortId}/custom-fields/${fieldId}`);
  },

  /**
   * Reorder custom field definitions
   */
  reorderCustomFields: async (cohortId: string, fieldIds: string[]): Promise<CohortCustomFieldDefinition[]> => {
    return apiClient.put<CohortCustomFieldDefinition[]>(`/api/cohorts/${cohortId}/custom-fields/reorder`, { fieldIds });
  },

  /**
   * Bulk update custom field values for multiple participants
   */
  bulkUpdateCustomFieldValues: async (
    cohortId: string,
    fieldId: string,
    updates: Array<{ participantId: string; value: string | null }>,
  ): Promise<{ updatedCount: number }> => {
    return apiClient.put<{ updatedCount: number }>(`/api/cohorts/${cohortId}/custom-fields/${fieldId}/values`, {
      updates,
    });
  },

  // ============================================================================
  // Concern Flag Methods
  // ============================================================================

  /**
   * Resolve a concern flag for a participant
   */
  resolveConcernFlag: async (
    cohortId: string,
    participantId: string,
    flagId: string,
    resolutionNotes?: string,
  ): Promise<void> => {
    return apiClient.patch<void>(
      `/api/cohorts/${cohortId}/participants/${participantId}/concern-flags/${flagId}`,
      { action: 'resolve', resolution_notes: resolutionNotes },
    );
  },

  /**
   * Dismiss a concern flag for a participant
   */
  dismissConcernFlag: async (
    cohortId: string,
    participantId: string,
    flagId: string,
  ): Promise<void> => {
    return apiClient.patch<void>(
      `/api/cohorts/${cohortId}/participants/${participantId}/concern-flags/${flagId}`,
      { action: 'dismiss' },
    );
  },

  // ============================================================================
  // MED-877: Internal Medley notes (admin + cohort_manager only on the server)
  // ============================================================================

  /**
   * Get internal Medley notes for a participant in this cohort.
   * Server returns 403 for non-admin/non-cohort-manager users.
   */
  getInternalNotes: async (
    cohortId: string,
    participantId: string,
  ): Promise<{ internal_notes: string | null }> => {
    return apiClient.get<{ internal_notes: string | null }>(
      `/api/cohorts/${cohortId}/participants/${participantId}/internal-notes`,
    );
  },

  /**
   * Save internal Medley notes for a participant. HTML is sanitized server-side.
   * Pass null or empty string to clear.
   */
  updateInternalNotes: async (
    cohortId: string,
    participantId: string,
    notes: string | null,
  ): Promise<{ internal_notes: string | null }> => {
    return apiClient.patch<{ internal_notes: string | null }>(
      `/api/cohorts/${cohortId}/participants/${participantId}/internal-notes`,
      { internal_notes: notes },
    );
  },
};

export default participantsApi;
